import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {LandingPageChoices} from 'utils/types';
import ApiService from 'redux/services/api-service';
import {ImpersonationProfile} from 'services/impersonation/service';

export interface IState {
  user: any;
  profile?: {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_staff: boolean;
    is_superuser: boolean;
    // True while the account is still on an admin-issued temporary password.
    must_change_password?: boolean;
    permissions: string[];
    // Set only while a staff user is viewing PathX as this user; everything else on this
    // object describes the user being viewed, not the one signed in.
    impersonation?: ImpersonationProfile | null;
    defaultCampaign?: any;
    preferences: any;
    show_landing_page: LandingPageChoices;
    /** The client this user belongs to; null for staff with no client attached. */
    client?: {id: number; name: string; hide_homepage_columns: string[]} | null;
  };
  loaded: boolean;
  isProfileLoading: boolean;
}

export const userEndpoints = {
  loginUser: createAsyncThunk('user/loginUser', async (payload: any) => {
    const response = await ApiService.post('/token/', payload);
    return response.data;
  }),
  registerUser: createAsyncThunk('user/registerUser', async (payload: any) => {
    const response = await ApiService.post('/sign-up/', payload);
    return response.data;
  }),
  getProfile: createAsyncThunk('user/getProfile', async () => {
    const response = await ApiService.get('/profile/');
    return response.data;
  }),
  setPassword: createAsyncThunk('user/setPassword', async (payload: {password: string}, {rejectWithValue}) => {
    try {
      const response = await ApiService.post('/set_password/', payload);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  }),
  updateProfile: createAsyncThunk('user/updateProfile', async (payload: any) => {
    const response = await ApiService.put('/profile/', payload);
    return response.data;
  }),
  resetPassword: createAsyncThunk('user/resetPassword', async (payload: any) => {
    const response = await ApiService.post('/password_reset/', payload);
    return response.data;
  }),
  validateResetPasswordToken: createAsyncThunk('user/validateResetPasswordToken', async (payload: any) => {
    const response = await ApiService.post('/password_reset/validate_token/', payload);
    return response.data;
  }),
  confirmResetPassword: createAsyncThunk('user/validateResetPasswordToken', async (payload: any) => {
    const response = await ApiService.post('/password_reset/confirm/', payload);
    return response.data;
  }),
  getStaffUserList: createAsyncThunk('user/getStaffUserList', async (search: string) => {
    const response = await ApiService.get(`/staff_user_list/?search=${search}`);
    return response.data;
  }),
  getNonStaffUserList: createAsyncThunk('user/getNonStaffUserList', async (search: string) => {
    const response = await ApiService.get(`/non_staff_user_list/?search=${search}`);
    return response.data;
  }),
  getStaffUser: createAsyncThunk('user/getStaffUser', async (id: any) => {
    const response = await ApiService.get(`/staff_user_list/${id}/`);
    return response.data;
  }),
  inviteUser: createAsyncThunk('user/inviteUser', async (payload: any) => {
    const response = await ApiService.post('/invite-user/', payload);
    return response.data;
  }),
  dismissLandingPage: createAsyncThunk('user/dismissLandingPage', async () => {
    const response = await ApiService.post('/user_data/dismiss-landing-page/');
    return response.data;
  }),
};
export const userSlice = createSlice({
  name: 'user',
  initialState: {
    user: {},
    profile: undefined,
    loaded: false,
    isProfileLoading: false,
  } as IState,
  reducers: {
    clearProfile(state) {
      state.profile = undefined;
      state.user = {};
      state.loaded = false;
      state.isProfileLoading = false;
    },
    setProfile(state, {payload}: PayloadAction<any>) {
      state.profile = payload || undefined;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(userEndpoints.loginUser.fulfilled, (state, {payload}) => {
        state.user = payload;
      })
      .addCase(userEndpoints.getProfile.pending, state => {
        state.isProfileLoading = true;
      })
      .addCase(userEndpoints.getProfile.rejected, state => {
        state.isProfileLoading = false;
      })
      .addCase(userEndpoints.getProfile.fulfilled, (state, {payload}) => {
        state.profile = {...payload};
        state.loaded = true;
        state.isProfileLoading = false;
      });
  },
});

export const userActions = userSlice.actions;

export type UserSlice = {
  [userSlice.name]: ReturnType<(typeof userSlice)['reducer']>;
};
